import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DayOfWeekEnum } from "@enums/dayOfWeek.enum";
import { ScheduledReportDateRangeEndEnum } from "@enums/scheduledReportDateRangeEnd.enum";
import { ScheduledReportDateRangeStartEnum } from "@enums/scheduledReportDateRangeStart.enum";
import { ScheduledReportReportTypeEnum } from "@enums/scheduledReportReportType.enum";
import { ScheduledReportScheduleTypeEnum } from "@enums/scheduledReportScheduleType.enum";
import { ScheduledReportSingleDayEnum } from "@enums/scheduledReportSingleDay.enum";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ScheduledReport } from "@interfaces/models/scheduledReport";
import { Site } from "@interfaces/site.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ScheduledReportService } from "@services/scheduledReport.service";
import { SiteService } from "@services/site.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "scheduledReports-id",
	styleUrls: ["scheduledReports-id.css"],
	templateUrl: "scheduledReports-id.html"
})
export class ScheduledReportsIdAdminComponent {
	daysOfWeek: any;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	isSuperAdmin = false;
	item: ScheduledReport = new ScheduledReport();
	itemId = "";
	itemType = "Scheduled Report";
	people: UserProfile[] = [];
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4] + "/" + location.href.split("/")[5];
	scheduledReportDateRangeEnds: any;
	scheduledReportDateRangeStarts: any;
	scheduledReportReportTypes: any;
	scheduledReportScheduleTypes: any;
	scheduledReportSingleDays: any;
	showDateRangeEndParameter = false;
	showDateRangeStartParameter = false;
	showMultipleSiteIdsParameter = false;
	showScheduleDayDropdown = false;
	showScheduleSingleDay = false;
	showSingleDayParameter = false;
	showSiteIdParameter = false;
	sites: Site[] = [];
	title: string = "View " + this.itemType;
	userProfileId: number;

	constructor(private appSettingsService: AppSettingsService, private router: Router, private route: ActivatedRoute, private itemService: ScheduledReportService, private siteService: SiteService, private toastService: ToastService, private userProfileService: UserProfileService) {
		this.buildButtons("view");
		this.getPeople();
		this.getSites();
		this.isSuperAdmin = CheckGroupPermissions(appSettingsService, "Super Admin");
		this.userProfileId = this.appSettingsService.get("profileId", false);
		this.daysOfWeek = GetPropertiesFromEnum(DayOfWeekEnum);
		this.scheduledReportDateRangeEnds = GetPropertiesFromEnum(ScheduledReportDateRangeEndEnum);
		this.scheduledReportDateRangeStarts = GetPropertiesFromEnum(ScheduledReportDateRangeStartEnum);
		this.scheduledReportReportTypes = GetPropertiesFromEnum(ScheduledReportReportTypeEnum);
		this.scheduledReportScheduleTypes = GetPropertiesFromEnum(ScheduledReportScheduleTypeEnum);
		this.scheduledReportSingleDays = GetPropertiesFromEnum(ScheduledReportSingleDayEnum);
		this.route.params
			.subscribe((params) => {
				if (params.id != "create") {
					if (params.id.split("_").length > 1) {
						this.getItem(params.id.split("_")[0]);
						this.edit();
					} else {
						this.getItem(params.id);
					}
				} else {
					this.create();
				}
			});
	}

	buildButtons(mode: string) {
		if (mode == "edit") {
			this.headerPrimaryButtons = [
				{ icon: "fal fa-save", method: "updateItem", text: "Save Changes" },
				{ icon: "fal fa-eye", method: "cancelEditing", text: "View Mode" },
				{ icon: "fal fa-times", method: "close", text: "Close" }
			];
		} else if (mode == "create") {
			this.headerPrimaryButtons = [
				{ icon: "fal fa-save", method: "createItem", text: "Save" },
				{ icon: "fal fa-times", method: "close", text: "Close" }
			];
		} else if (mode == "view") {
			this.headerPrimaryButtons = [
				{ icon: "fal fa-pencil", method: "edit", text: "Edit" },
				{ icon: "fal fa-times", method: "close", text: "Close" }
			];	
		}
	}

	cancelEditing() {
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
	}

	close() {
		this.router.navigate([`/${this.returnUrl}/`]);
	}

	create() {
		this.readOnly = false;
		this.buildButtons("create");
		this.title = "Create New " + this.itemType;
		this.item.userProfileId = this.userProfileId;
		this.item.siteIdsSelection = [];
	}

	createItem() {
		this.prepareItemForSaving();
		this.itemService.createSingle(this.item)
			.subscribe(
				() => {
					this.toastService.success("Successfully Created "+ this.itemType);
					this.close();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	edit() {
		this.readOnly = false;
		this.buildButtons("edit");
		this.title = "Edit " + this.itemType;
		this.toastService.info("You are now editing...");
	}
	
	getItem(itemId: number) {
		this.itemService.getSingleById(itemId)
			.subscribe(
				(res: any) => {
					this.item = res.response;
					if (this.item.siteIdsSelection === null) { this.item.siteIdsSelection = []; }
					this.item.siteIdsSelection = JSON.parse(this.item.siteIds);
					this.setVisibleParameters(this.item.reportType);
					this.setScheduleDayVisible(this.item.scheduleType);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}
	
	getPeople() {
		this.userProfileService.getAllWithUsers()
			.subscribe(
				(res: any) =>  {
					this.people = res.response;
					this.people.sort((a,b) => a.fullName.localeCompare(b.fullName));
				},
				(err) => console.log(err)
			);
	}

	getSites() {
		this.siteService.getAll()
			.subscribe(
				(res: any) => {
					this.sites = res.response.items;
					this.sites.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	prepareItemForSaving() {
		this.item.siteIds = JSON.stringify(this.item.siteIdsSelection);
	}

	scheduledReportParamsChanged(args: any) {
		switch(args.dataField) {
			case "reportType":
				this.setVisibleParameters(args.value);
				break;
			case "scheduleType":
				this.setScheduleDayVisible(args.value);
				break;
		}
	}

	setScheduleDayVisible(scheduleType: ScheduledReportScheduleTypeEnum) {
		this.showScheduleSingleDay = scheduleType === ScheduledReportScheduleTypeEnum.OnDayOfMonth;
		this.showScheduleDayDropdown = scheduleType === ScheduledReportScheduleTypeEnum.OnWeekday;
	}

	setVisibleParameters(reportType: ScheduledReportReportTypeEnum) {
		this.showDateRangeEndParameter = false;
		this.showDateRangeStartParameter = false;
		this.showMultipleSiteIdsParameter = false;
		this.showSingleDayParameter = false;
		this.showSiteIdParameter = false;
		switch (reportType) {
			case ScheduledReportReportTypeEnum.OutsideProcessContractsSentButNotReceived:
				// this report has no parameters
				break;
		}
	}

	updateItem() {
		this.prepareItemForSaving();
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => {
					this.toastService.success("Successfully Updated "+ this.itemType);
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}
}